// 담당: 현정
// Creators — Overview 섹션 (6블록).
//   ① 에디토리얼 히어로 + Branded Engagement 배지
//   ② 오디언스 스트립 (3채널 총 구독)
//   ③ Branded Content Features 캐러셀
//   ④ 채널 브랜디드 퍼포먼스 (Long/Short)  ⑤ 매출 추이 차트(mock)
//   ⑥ Confirmed Business | Recent Collaborations
//
// 데이터 연결(creator-slots와 동일 방식): 실 API(fetchOverview)만 호출. mock 폴백 없음.
//   dev 경로: 브라우저 → vite 프록시 → 8085 직결(+injectToken env). env 토큰 없으면 401.
//   · 성공(200) → 실데이터 렌더
//   · 401 + dev → "로그인이 필요합니다" + Bearer 토큰 입력창(붙여넣고 재조회 → localStorage → apiGet Bearer)
//   · 401 + prod → "로그인이 필요합니다"만
//   · 그 외/연결실패(500·네트워크·서버 다운) → "데이터를 불러오지 못했습니다" + 상태·메시지 + 재시도
//   → 어떤 경우에도 가짜 데이터가 안 뜬다(mock 없음). 서버 내려있으면 에러 화면.
// creator(프로필/테마)는 셸(CreatorsPage)에서 prop으로 내려받는다.
import { useEffect, useRef, useState } from 'react'
import {
  fetchOverview,
  setLecommendToken,
  type CreatorSummary,
  type OverviewSlice,
  type Platform,
} from '../creatorsApi'
import { ALL_PLATFORMS, channelSamples, pickDataPlatform } from './platformDefault'
import { usePlatformData } from './usePlatformData'
import { ProfileHero } from './overview/ProfileHero'
import { AudienceStrip } from './overview/AudienceStrip'
import { BrandedContentCarousel } from './overview/BrandedContentCarousel'
import { ChannelPerformance } from './overview/ChannelPerformance'
import { RevenueTrendChart } from './overview/RevenueTrendChart'
import { ConfirmedRecent } from './overview/ConfirmedRecent'

interface Props {
  creatorId: string
  creator: CreatorSummary | null
  /** 셸이 URL 로 쥔 플랫폼 — 섹션을 옮겨도 유지된다. 탭은 여전히 **블록이** 소유한다
   *  (오디언스 스트립·채널 퍼포먼스는 3채널을 함께 보여주므로 탭이 붙지 않는다). */
  platform: Platform
  /** 사용자가 정했거나 이미 보정된 값인가. false 일 때만 첫 응답에서 한 번 보정한다. */
  platformPinned: boolean
  onPlatformChange: (platform: Platform) => void
}

export function OverviewSection({
  creatorId,
  creator,
  platform: initialPlatform,
  platformPinned,
  onPlatformChange,
}: Props) {
  // 탭은 **블록마다 따로** 선다. 협찬 콘텐츠와 최근 협업은 답하는 질문이 다르므로
  // ("이 채널에서 뭐가 잘 됐나" vs "이 채널에서 최근에 뭘 했나"), 한쪽을 바꿨다고 다른 쪽이
  // 따라 움직이면 안 본 화면이 바뀐다. 세 채널을 미리 받아두므로 전환은 양쪽 다 즉시다.
  const [contentPlatform, setContentPlatform] = useState<Platform>(initialPlatform)
  const [recentPlatform, setRecentPlatform] = useState<Platform>(initialPlatform)
  const [tokenInput, setTokenInput] = useState('')
  const correctedRef = useRef(false) // 기본 채널 보정은 딱 한 번

  // 한 번 조회로 세 채널분이 온다 — 탭은 그중 무엇을 보여줄지만 고른다(전환 시 조회 없음).
  const overviews = usePlatformData(creatorId, fetchOverview, (d) => d.byPlatform)
  const data = overviews.data
  const error = overviews.error
  // 구버전 BE(byPlatform 미도달)면 최상위 필드로 폴백한다 — 배포 순서와 무관하게 화면이 안 빈다.
  const fallbackSlice: OverviewSlice | undefined = data
    ? {
        brandedEngagement: data.brandedEngagement,
        brandedEngagementSampleSize: data.brandedEngagementSampleSize ?? 0,
        brandedContent: data.brandedContent,
        meta: data.meta,
        recentCollaborations: data.recentCollaborations,
      }
    : undefined
  const sliceOf = (p: Platform): OverviewSlice | undefined => overviews.slice(p) ?? fallbackSlice
  const contentData = sliceOf(contentPlatform)
  const recentData = sliceOf(recentPlatform)

  // 배지는 **세 채널을 한 번에** 보여준다 — 캐러셀 탭을 따라가면, 탭을 만진 적 없는 사람이
  // 한 채널 값을 크리에이터 전체 값으로 읽는다.
  const engagementRows = ALL_PLATFORMS.flatMap((p) => {
    const slice = overviews.slice(p)
    if (!slice) return []
    return [{ platform: p, value: slice.brandedEngagement, sample: slice.brandedEngagementSampleSize }]
  })

  // 열자마자 빈 화면 방지 — 채널 퍼포먼스는 **세 채널 전부**의 표본을 싣고 오므로, 지금 채널에
  // 표본이 0이고 다른 채널엔 있으면 그쪽으로 한 번 옮긴다. URL 이 고정이면 사용자 선택이라 그대로 둔다.
  useEffect(() => {
    if (platformPinned || correctedRef.current || !data) return
    correctedRef.current = true
    const better = pickDataPlatform(initialPlatform, channelSamples(data.channelPerformance))
    if (better) {
      // 보정은 **둘 다** 옮긴다 — 아직 사용자가 만진 적 없는 초기 상태라 갈라둘 이유가 없다.
      // (이후 탭 클릭부터는 각자 논다.)
      setContentPlatform(better)
      setRecentPlatform(better)
      onPlatformChange(better)
    }
  }, [data, initialPlatform, platformPinned, onPlatformChange])

  function submitToken() {
    if (!tokenInput.trim()) return
    setLecommendToken(tokenInput.trim())
    setTokenInput('')
    correctedRef.current = false
    overviews.reload()
  }

  if (error) {
    const isAuth = error.status === 401
    return (
      <div className="border border-line-hairline bg-surface-base p-8">
        <div className="font-display text-[16px] font-bold text-fg-1">
          {isAuth ? '로그인이 필요합니다' : '데이터를 불러오지 못했습니다'}
        </div>
        <div className="mt-2 font-text text-[14px] text-fg-3">
          {isAuth
            ? 'Leferi Apps 로그인이 필요합니다.'
            : `${error.message}${error.transport && error.status ? ` (${error.status})` : ''}`}
        </div>

        {/* 로컬 dev 실연결 테스트 — Bearer 토큰 붙여넣기 (creator-slots와 동일) */}
        {isAuth && import.meta.env.DEV && (
          <div className="mt-4 max-w-md">
            <div className="mb-2 font-text text-[12px] text-fg-3">로컬 dev — Bearer 토큰으로 실연결 테스트</div>
            <div className="flex gap-2">
              <input
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Bearer 토큰 붙여넣기"
                className="flex-1 rounded-r-1 border border-line-soft bg-surface-base px-2 py-1 text-fg-1 focus:shadow-focus focus:outline-none"
              />
              <button
                type="button"
                onClick={submitToken}
                className="rounded-r-1 bg-brand-blue px-3 py-1 font-medium text-fg-inverse hover:opacity-90 focus:shadow-focus focus:outline-none"
              >
                저장 후 재조회
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={overviews.reload}
          className="mt-4 rounded-r-1 border border-line-soft px-3 py-1 font-text text-[14px] text-fg-1 hover:bg-surface-soft focus:shadow-focus focus:outline-none"
        >
          다시 시도
        </button>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="border border-line-hairline bg-surface-base p-8 font-text text-[14px] text-fg-3">
        {overviews.loading ? '불러오는 중…' : '표시할 데이터가 없습니다.'}
      </div>
    )
  }

  return (
    <div>
      {/* ①+② 히어로와 오디언스 스트립은 붙어서 한 덩어리 */}
      {/* 배지 값도 협찬 콘텐츠와 같은 채널이어야 한다 — 값은 A 채널, 라벨은 B 채널이면 거짓말이다.
          히어로엔 탭을 붙일 자리가 없어(이미지 위 오버레이) 캐러셀 탭을 따라간다. */}
      {creator && (
        <ProfileHero creator={creator} engagement={engagementRows} meta={data.meta} />
      )}
      <AudienceStrip
        totalSubscribers={data.totalSubscribers}
        channels={data.channels}
        themeColor={creator?.themeColor ?? ''}
      />

      {/* ③ 협찬 콘텐츠 캐러셀 */}
      {/* meta 는 **채널별 조각**에서 가져온다. 최상위 meta 는 bi_base_data(YouTube 전용 레거시)
          기준이라 IG·TikTok 탭에서 쓰면 화면이 남의 채널 동기화 시각을 자기 것처럼 단언한다
          (실측: IG 데이터가 최신인데 "수집 2025.10.14"). BE 는 채널별 meta 를 byPlatform 에 이미
          내려준다(IG·TikTok = creator_social_media). 구버전 BE 에서만 최상위로 폴백한다. */}
      <BrandedContentCarousel
        items={contentData?.brandedContent ?? []}
        meta={contentData?.meta ?? data.meta}
        platform={contentPlatform}
        onPlatformChange={setContentPlatform}
        // 한 번에 다 받아두므로 탭 전환엔 대기가 없다. 첫 로딩 때만 뜬다.
        busy={overviews.loading}
        channels={data.channels}
      />

      {/* ④ 채널 퍼포먼스 + ⑤ 매출 추이 : 2열(auto-fit) */}
      <div className="mt-8 grid items-start gap-5 [grid-template-columns:repeat(auto-fit,minmax(360px,1fr))]">
        <ChannelPerformance channels={data.channelPerformance} meta={data.meta} />
        <RevenueTrendChart points={data.revenueTrend} />
      </div>

      {/* ⑥ Confirmed | Recent */}
      {/* 확정 비즈니스(SF)는 채널 무관이라 최상위, 최근 협업은 채널 탭을 타므로 조각에서 읽는다. */}
      <ConfirmedRecent
        confirmed={data.confirmedBusiness}
        recent={recentData?.recentCollaborations ?? []}
        platform={recentPlatform}
        onPlatformChange={setRecentPlatform}
        busy={overviews.loading}
        channels={data.channels}
      />
    </div>
  )
}
